import { useEffect, useState } from 'react';
import { analytics } from '@mindstudio-ai/interface';
import api from '../../api';

/**
 * Two small sign-in conveniences shared by every code-sign-in surface (the
 * /signin page, the sign-in popup and the drawings checkout sheet):
 *
 * 1. Remember the last email that signed in on this device, so a returning
 *    member finds it already filled in (with a "Forget" link for shared devices).
 * 2. After a code is sent, an "Open Gmail" / "Open Outlook" / … button that
 *    opens their inbox (searched to recent mail where the provider supports it),
 *    so they don't have to go hunting for the email.
 */

const KEY = 'ed.lastSignInEmail';

export function getRememberedEmail(): string {
  try {
    return localStorage.getItem(KEY) ?? '';
  } catch {
    return '';
  }
}

export function rememberEmail(email: string): void {
  try {
    if (email.includes('@')) {
      localStorage.setItem(KEY, email.trim().toLowerCase());
      localStorage.setItem(METHOD_KEY, 'email');
    }
  } catch {
    /* private mode */
  }
}

const PHONE_KEY = 'ed.lastSignInPhone';
const METHOD_KEY = 'ed.lastSignInMethod';

/** The phone (as typed, with its country) that last signed in on this device. */
export function getRememberedPhone(): { country: string; phone: string } | null {
  try {
    const v = JSON.parse(localStorage.getItem(PHONE_KEY) ?? 'null');
    return v && typeof v.phone === 'string' && typeof v.country === 'string' ? v : null;
  } catch {
    return null;
  }
}

export function rememberPhone(country: string, phone: string): void {
  try {
    if (phone.trim()) {
      localStorage.setItem(PHONE_KEY, JSON.stringify({ country, phone: phone.trim() }));
      localStorage.setItem(METHOD_KEY, 'phone');
    }
  } catch {
    /* private mode */
  }
}

export function forgetRememberedPhone(): void {
  try {
    localStorage.removeItem(PHONE_KEY);
  } catch {
    /* private mode */
  }
}

/** How the member last signed in here, if what they used is still remembered. */
export function getLastSignInMethod(): 'email' | 'phone' | null {
  try {
    const m = localStorage.getItem(METHOD_KEY);
    if (m === 'phone' && getRememberedPhone()) return 'phone';
    if (m === 'email' && getRememberedEmail()) return 'email';
    // Before the method was recorded, an email may already be remembered.
    if (getRememberedEmail()) return 'email';
    return null;
  } catch {
    return null;
  }
}

export function forgetRememberedEmail(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* private mode */
  }
}

type Provider = 'google' | 'microsoft' | 'yahoo' | 'icloud' | 'proton';

const LABEL: Record<Provider, string> = {
  google: 'Open Gmail',
  microsoft: 'Open Outlook',
  yahoo: 'Open Yahoo Mail',
  icloud: 'Open iCloud Mail',
  proton: 'Open Proton Mail',
};

function inboxUrl(provider: Provider, email: string): string {
  switch (provider) {
    case 'google':
      // authuser picks the right Google account when several are signed in; the search
      // shows the last hour across every folder, spam included.
      return `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(email)}#search/${encodeURIComponent('in:anywhere newer_than:1h')}`;
    case 'microsoft':
      return /@(outlook|hotmail|live|msn)\./i.test(email) ? 'https://outlook.live.com/mail/0/' : 'https://outlook.office.com/mail/';
    case 'yahoo':
      return 'https://mail.yahoo.com/';
    case 'icloud':
      return 'https://www.icloud.com/mail';
    case 'proton':
      return 'https://mail.proton.me/u/0/inbox';
  }
}

/** Per-session cache so re-sends don't repeat the lookup. */
const cache = new Map<string, Provider | null>();

/**
 * "Open Gmail" (etc.) for the address a code was just sent to. Renders nothing
 * until the provider is known, inside a fixed-height slot so nothing shifts.
 */
export function OpenInboxButton({ email, className }: { email: string; className?: string }) {
  const domain = email.split('@')[1]?.trim().toLowerCase() ?? '';
  const [provider, setProvider] = useState<Provider | null>(() => cache.get(domain) ?? null);

  useEffect(() => {
    if (!domain) return;
    if (cache.has(domain)) {
      setProvider(cache.get(domain) ?? null);
      return;
    }
    let alive = true;
    api
      .detectMailProvider({ domain })
      .then(({ provider: p }) => {
        cache.set(domain, p);
        if (alive) setProvider(p);
      })
      .catch(() => cache.set(domain, null));
    return () => {
      alive = false;
    };
  }, [domain]);

  return (
    <div className="si-inbox" aria-live="polite">
      {provider && (
        <a
          className={className ?? 'si-inbox__btn'}
          href={inboxUrl(provider, email)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => analytics.track('signin_open_inbox', { provider })}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
          {LABEL[provider]}
        </a>
      )}
      <style>{SI_CSS}</style>
    </div>
  );
}

/** "Not you?" under a pre-filled email or phone field; forgets it on this device. */
export function ForgetEmailLink({ onForget, kind = 'email' }: { onForget: () => void; kind?: 'email' | 'phone' }) {
  return (
    <>
    <style>{SI_CSS}</style>
    <button
      type="button"
      className="si-forget"
      onClick={() => {
        if (kind === 'phone') forgetRememberedPhone();
        else forgetRememberedEmail();
        onForget();
        analytics.track('signin_forget_email', { kind });
      }}
    >
      Not you? Use a different {kind === 'phone' ? 'number' : 'email'}
    </button>
    </>
  );
}

const SI_CSS = `
.si-inbox { min-height: 44px; display: flex; justify-content: center; align-items: center; margin-top: 8px; }
.si-inbox__btn {
  display: inline-flex; align-items: center; gap: 8px; height: 40px; padding: 0 18px;
  border-radius: 999px; border: 1px solid var(--border-hairline, rgba(15,23,20,.14));
  background: var(--surface-card, #fbf8f0); color: var(--heartwood, #1b3a2e);
  font: 600 14px var(--f-sans, inherit); text-decoration: none; animation: siIn 160ms ease both;
}
.si-inbox__btn:hover { border-color: var(--heartwood, #1b3a2e); }
.si-forget { margin-top: 6px; padding: 0; border: 0; background: none; cursor: pointer; font: 500 12.5px var(--f-sans, inherit); color: var(--text-decorative); text-decoration: underline; text-underline-offset: 3px; }
.si-forget:hover { color: var(--ink); }
@keyframes siIn { from { opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .si-inbox__btn { animation: none; } }
`;
